
import React from 'react';
import NavBar from '@/components/NavBar';
import SEOHead from '@/components/SEOHead';
import BreadcrumbSEO from '@/components/BreadcrumbSEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpCircle, Crown, Check, Zap, Shield, Star } from 'lucide-react';

const FAQ = () => {
  const faqItems = [
    {
      question: "¿Cómo funciona el compresor de imágenes?",
      answer: "Nuestro compresor utiliza algoritmos avanzados para reducir el tamaño de archivo de tus imágenes JPEG sin comprometer significativamente la calidad visual. El proceso es completamente automático y se realiza en tu navegador."
    },
    {
      question: "¿Qué formatos de imagen son compatibles?",
      answer: "Soportamos archivos JPEG, JPG, PNG y WebP para todos los usuarios de forma gratuita. Los usuarios Premium además disfrutan de resolución 4K (hasta 4096x2160px), compresión ilimitada y otras ventajas exclusivas."
    },
    {
      question: "¿Cuál es el límite de tamaño de archivo?",
      answer: "Para usuarios gratuitos, el límite es de 10MB por imagen. Los usuarios Premium pueden subir imágenes de hasta 50MB."
    },
    {
      question: "¿Cuántas imágenes puedo comprimir por día?",
      answer: "Los usuarios gratuitos pueden comprimir hasta 10 imágenes por día, con o sin registro. Con el plan Premium, no hay límites en el número de imágenes que puedes comprimir."
    },
    {
      question: "¿Qué ventajas ofrece el plan Premium?",
      answer: "El plan Premium incluye múltiples beneficios: compresión ilimitada de imágenes sin límites diarios, soporte para archivos más grandes (hasta 50MB), compatibilidad con formatos adicionales (PNG, WebP y otros), calidad de compresión superior (hasta 95%), procesamiento prioritario más rápido, e interfaz sin anuncios."
    },
    {
      question: "¿Se almacenan mis imágenes en sus servidores?",
      answer: "No, todas las imágenes se procesan localmente en tu navegador. Tus imágenes nunca se envían a nuestros servidores, garantizando tu privacidad y seguridad."
    },
    {
      question: "¿Puedo ajustar el nivel de compresión?",
      answer: "Sí, puedes ajustar la calidad de compresión desde 10% hasta 100%. Un porcentaje menor resulta en archivos más pequeños pero con menor calidad visual."
    },
    {
      question: "¿Cómo puedo cancelar mi suscripción Premium?",
      answer: "Puedes cancelar tu suscripción en cualquier momento desde tu perfil de usuario. La suscripción seguirá activa hasta el final del período de facturación actual."
    },
    {
      question: "¿La compresión afecta los metadatos de la imagen?",
      answer: "Por defecto, preservamos los metadatos básicos como la orientación. Sin embargo, algunos metadatos EXIF pueden eliminarse durante el proceso de compresión para reducir el tamaño del archivo."
    },
    {
      question: "¿Puedo usar el servicio en dispositivos móviles?",
      answer: "Sí, nuestra aplicación está optimizada para funcionar en dispositivos móviles, tablets y computadoras de escritorio. La interfaz se adapta automáticamente al tamaño de tu pantalla."
    }
  ];

  const premiumBenefits = [
    { icon: Zap, title: "Compresión Ilimitada", description: "Sin límites diarios de imágenes" },
    { icon: Shield, title: "Archivos Grandes", description: "Hasta 50MB por imagen" },
    { icon: Star, title: "Resolución 4K", description: "Hasta 4096x2160px" },
    { icon: Crown, title: "Calidad Superior", description: "Hasta 95% de calidad" },
    { icon: Check, title: "Procesamiento Rápido", description: "Prioridad en el procesamiento" },
    { icon: Star, title: "Sin Anuncios", description: "Interfaz limpia y sin distracciones" }
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <SEOHead 
        title="Preguntas Frecuentes - Compresor de Imágenes | CompresorJPEG"
        description="Encuentra respuestas a preguntas frecuentes sobre compresión de imágenes JPG, PNG, WebP. Guía completa para optimizar tus fotos online gratis."
        keywords="preguntas comprimir imagenes, dudas compresor jpg, como comprimir png, ayuda optimizar webp, faq compresion fotos"
        canonical="https://compresorjpeg.com/faq"
      />
      <NavBar />
      
      <div className="container mx-auto px-4 py-8">
        <BreadcrumbSEO />
        
        {/* Header */}
        <div className="text-center mb-8">
...
        </div>

        {/* Premium Benefits Highlight */}
        <div className="mb-12">
          <Card className="border-2 border-gradient-to-r from-yellow-400 to-orange-500 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-xl">
            <CardHeader className="text-center pb-4">
              <div className="flex items-center justify-center mb-2">
                <Crown className="h-8 w-8 text-yellow-600 mr-2" />
                <CardTitle className="text-3xl font-bold text-gray-800">
                  Beneficios Premium
                </CardTitle>
              </div>
              <CardDescription className="text-gray-600 text-lg">
                Desbloquea todo el potencial de nuestro compresor de imágenes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {premiumBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm border border-yellow-200">
                    <benefit.icon className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">{benefit.title}</h4>
                      <p className="text-sm text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Además incluye: interfaz sin anuncios y más funciones exclusivas
                </p>
                <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full font-medium shadow-lg">
                  <Crown className="h-4 w-4 mr-2" />
                  ¡Actualiza a Premium hoy!
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Content */}
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">
                ¿Tienes alguna pregunta?
              </CardTitle>
              <CardDescription className="text-center">
                Aquí encontrarás las respuestas a las preguntas más frecuentes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {faqItems.map((item, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Contact Section */}
          <Card className="mt-8">
            <CardHeader className="text-center">
              <CardTitle>¿No encontraste lo que buscabas?</CardTitle>
              <CardDescription>
                Si tienes alguna otra pregunta, no dudes en contactarnos
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-4">
                Estamos aquí para ayudarte con cualquier duda sobre nuestro servicio de compresión de imágenes.
              </p>
              <div className="space-y-2">
                <p className="text-sm text-gray-500">
                  Email: soporte@compresorjpeg.com
                </p>
                <p className="text-sm text-gray-500">
                  Horario de atención: Lunes a Viernes, 9:00 AM - 6:00 PM (UTC-5)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
